// UploadContext.js
import React, { createContext, useReducer, useContext, useEffect } from 'react';
import { setErrorPage } from 'services/requests/helpers/setErrorPage';
import { useAssessmentContext } from 'services/context/assessmentContext/useAssessmentContext';
import { UPLOAD_STATUSES } from 'constants/uploadStatuses';
import { updateFileStatus } from 'utils/indexedDBService';
import MyWorker from './uploadFiles.worker';

const UploadContext = createContext();

const initialState = {
  uploads: {},
  worker: null, // Add worker to the state
  pendingUploads: 0, // Track pending uploads
  idbUpdateTimestamp: null // Add idbUpdateTimestamp to the state
};

function uploadReducer(state, action) {
  const { type, payload } = action;
  switch (type) {
    case 'INIT_WORKER':
      return { ...state, worker: payload.worker };
    case 'SEND_FILE_TO_WORKER':
      state.worker.postMessage({ type, payload });
      return {
        ...state,
        uploads: {
          ...state.uploads,
          [payload.compositeId]: {
            status: UPLOAD_STATUSES.UPLOADING,
            error: null
          }
        }
      };
    case 'SEND_FILE_TO_WORKER_SUCCESS':
      return {
        ...state,
        uploads: {
          ...state.uploads,
          [payload.compositeId]: { status: UPLOAD_STATUSES.DONE, error: null } // Use compositeId
        },
        pendingUploads: state.pendingUploads - 1
      };
    case 'SEND_FILE_TO_WORKER_ERROR':
      return {
        ...state,
        uploads: {
          ...state.uploads,
          [payload.compositeId]: {
            status: UPLOAD_STATUSES.ERROR,
            error: payload.error
          }
        },
        pendingUploads: state.pendingUploads - 1
      };
    case 'IDB_UPDATED':
      return { ...state, idbUpdateTimestamp: Date.now() };
    default:
      return state;
  }
}

export function UploadProvider({ children }) {
  const [state, dispatch] = useReducer(uploadReducer, initialState);
  const { dispatch: assessmentDispatch } = useAssessmentContext();

  useEffect(() => {
    const worker = new MyWorker();
    dispatch({ type: 'INIT_WORKER', payload: { worker } });

    worker.onmessage = event => {
      // Handle messages from worker
      if (
        event.data.type === 'SEND_FILE_TO_WORKER_SUCCESS' ||
        event.data.type === 'SEND_FILE_TO_WORKER_ERROR'
      ) {
        updateIndexedDB(event.data);
      }
      if (event.data.type === 'UPLOAD_FILE_ERROR') {
        setErrorPage(
          {
            data: {
              message: `We got an error saving your file. Do not refresh or continue this assessment attempt as other files may be impacted as well.`
            },
            status: event.data.payload.status || '500'
          },
          assessmentDispatch
        );
      }
      dispatch(event.data); // Dispatch worker's action
    };

    const updateIndexedDB = async action => {
      try {
        if (action.type === 'SEND_FILE_TO_WORKER_SUCCESS') {
          await updateFileStatus(
            action.payload.compositeId,
            UPLOAD_STATUSES.DONE,
            false,
            dispatch
          );
        } else if (action.type === 'SEND_FILE_TO_WORKER_ERROR') {
          await updateFileStatus(
            action.payload.compositeId,
            UPLOAD_STATUSES.ERROR,
            action.payload.error,
            dispatch
          );
        }
      } catch (error) {
        console.error('Error updating IndexedDB:', error);
      }
    };

    return () => {
      worker.terminate();
    };
  }, [assessmentDispatch]);

  return (
    <UploadContext.Provider value={{ state, dispatch }}>
      {children}
    </UploadContext.Provider>
  );
}

export function useUploadContext() {
  return useContext(UploadContext);
}
